import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import authService from './authService';
import authStorage from '../../utils/authStorage';

const parsedUser = authStorage.getUser();
const user = parsedUser
  ? { ...parsedUser, userId: parsedUser.id }
  : null;
const token = authStorage.getToken();

const initialState = {
  user: user ? user : null,
  token: token ? token : null,
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: '',
};

// Login user
export const login = createAsyncThunk(
  'auth/login',
  async (userCredentials, thunkAPI) => {
    try {
      return await authService.login(userCredentials);
    } catch (error) {
      // Log the full error so developers can trace the exact cause in DevTools
      console.error('[auth/login] rejected:', error);
      // error is always an AuthError here (thrown by authService)
      // error.message already contains the exact message from the backend / network layer
      const message = error.message || 'Login failed. Please try again.';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Logout user
export const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    try {
      const refreshToken = authStorage.getRefreshToken();
      if (refreshToken) {
        await authService.logout(refreshToken);
      }
      return {};
    } catch (error) {
      console.error('[auth/logout] error (session cleared anyway):', error);
      return {};
    }
  }
);

// Refresh token
export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, thunkAPI) => {
    try {
      const refreshToken = authStorage.getRefreshToken();
      if (!refreshToken) {
        return thunkAPI.rejectWithValue('No refresh token available');
      }
      return await authService.refreshToken(refreshToken);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.error) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Change password on first login
export const changePasswordFirstLogin = createAsyncThunk(
  'auth/changePasswordFirstLogin',
  async ({ userId, tempPassword, newPassword }, thunkAPI) => {
    try {
      return await authService.changePasswordFirstLogin(userId, tempPassword, newPassword);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.error) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = '';
    },
    clearError: (state) => {
      state.isError = false;
      state.message = '';
    },
    // Add auto-logout action for token expiration
    autoLogout: (state) => {
      state.user = null;
      state.token = null;
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;  // Don't set error for auto-logout to prevent showing on login page
      state.message = '';  // Clear message to prevent stale errors
      
      authStorage.clear();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        
        // Check if password change is required
        if (action.payload.mustChangePassword) {
          // Store minimal user info for password change flow
          state.user = {
            mustChangePassword: true,
            userId: action.payload.userId,
            username: action.payload.username
          };
          state.token = null; // No token until password is changed
        } else {
          // Normal login flow
          const userData = action.payload.user;
          
          // Ensure userId is available for consistent claim filtering
          const processedUser = {
            ...userData,
            userId: userData.id  // Backend always returns id as the primary key
          };
          
          state.user = processedUser;
          state.token = action.payload.accessToken;
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
        state.user = null;
        state.token = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isLoading = false;
        state.isSuccess = false;
        state.isError = false;
        state.message = '';
        
        authStorage.clear();
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.accessToken;
        state.isError = false;
        state.message = '';
      })
      .addCase(refreshToken.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.isError = false;  // Don't set error to prevent showing on login page
        state.message = '';  // Clear message
        
        authStorage.clear();
      })
      .addCase(changePasswordFirstLogin.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(changePasswordFirstLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false; // Don't trigger auto-redirect
        state.message = 'Password changed successfully';
        
        // Clear user state to prevent automatic authentication
        state.user = null;
        state.token = null;
        
        // Don't store tokens automatically - force manual re-login
        // This prevents infinite redirect loops between /change-password and /login
        authStorage.clear();
      })
      .addCase(changePasswordFirstLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      });
  },
});

export const { reset, clearError, autoLogout } = authSlice.actions;
export default authSlice.reducer;

// Base selectors
export const selectCurrentUser = (state) => state.auth.user;
export const selectCurrentToken = (state) => state.auth.token;
export const selectIsAuthenticated = (state) => !!state.auth.user && !!state.auth.token;

// Memoized selectors to prevent unnecessary re-renders
export const selectUserRoles = createSelector(
  [selectCurrentUser],
  (user) => user?.userRoles?.map(ur => ur.role?.name) || []
);

export const selectUserPermissions = createSelector(
  [selectCurrentUser],
  (user) => {
    const userRoles = user?.userRoles || [];
    const permissions = new Set();
    
    userRoles.forEach(ur => {
      ur.role?.rolePermissions?.forEach(rp => {
        permissions.add(`${rp.permission?.resource}:${rp.permission?.action}`);
      });
    });
    
    return Array.from(permissions);
  }
);

// Role checking functions with memoization
export const selectHasRole = (state, roleName) => {
  const roles = state.auth.user?.userRoles?.map(ur => ur.role?.name) || [];
  return roles.includes(roleName);
};

export const selectHasAnyRole = (state, roleNames) => {
  const roles = state.auth.user?.userRoles?.map(ur => ur.role?.name) || [];
  return roleNames.some(role => roles.includes(role));
};

export const selectHasPermission = (state, permission) => {
  const userRoles = state.auth.user?.userRoles || [];
  const permissions = new Set();
  
  userRoles.forEach(ur => {
    ur.role?.rolePermissions?.forEach(rp => {
      permissions.add(`${rp.permission?.resource}:${rp.permission?.action}`);
    });
  });
  
  const allPermissions = Array.from(permissions);
  const hasPermission = allPermissions.includes(permission);
  
  return hasPermission;
};

// Memoized role selectors - fixed to avoid identity function warnings
export const selectIsAdmin = createSelector(
  [selectCurrentUser],
  (user) => {
    const roles = user?.userRoles?.map(ur => ur.role?.name) || [];
    return roles.includes('Admin');
  }
);

export const selectIsHR = createSelector(
  [selectCurrentUser],
  (user) => {
    const roles = user?.userRoles?.map(ur => ur.role?.name) || [];
    return roles.includes('HR');
  }
);

export const selectIsBroker = createSelector(
  [selectCurrentUser],
  (user) => {
    const roles = user?.userRoles?.map(ur => ur.role?.name) || [];
    return roles.includes('Broker');
  }
);

export const selectIsClaimsOfficer = createSelector(
  [selectCurrentUser],
  (user) => {
    const roles = user?.userRoles?.map(ur => ur.role?.name) || [];
    return roles.includes('Claims_Officer');
  }
);

export const selectIsApprover = createSelector(
  [selectCurrentUser],
  (user) => {
    const roles = user?.userRoles?.map(ur => ur.role?.name) || [];
    return roles.includes('Approver');
  }
);

export const selectIsFinance = createSelector(
  [selectCurrentUser],
  (user) => {
    const roles = user?.userRoles?.map(ur => ur.role?.name) || [];
    return roles.includes('Finance');
  }
);

export const selectIsCRM = createSelector(
  [selectCurrentUser],
  (user) => {
    const roles = user?.userRoles?.map(ur => ur.role?.name) || [];
    return roles.includes('CRM');
  }
);

// Memoized permission checking for common actions - fixed to avoid identity warnings
export const selectCanManageUsers = createSelector(
  [selectCurrentUser],
  (user) => {
    const roles = user?.userRoles?.map(ur => ur.role?.name) || [];
    return roles.includes('Admin');
  }
);

// JWT-based permission selectors
export const selectJWTPermissions = createSelector(
  [(state) => state.auth.token],
  (token) => {
    if (!token) return [];
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.permissions || [];
  } catch {
    return [];
    }
  }
);

export const selectHasJWTPermission = (state, permission) => {
  const permissions = selectJWTPermissions(state);
  return permissions.includes(permission);
};

export const selectCanViewStatistics = createSelector(
  [selectJWTPermissions],
  (permissions) => {
    // Check for any statistics-related permissions
    return permissions.some(permission => 
      permission.includes('claims:') || 
      permission.includes('users:read') ||
      permission === 'system:monitor'
    );
  }
);

// Permission-based selectors that match the seeded permissions
export const selectCanManageUsersJWT = createSelector(
  [selectJWTPermissions],
  (permissions) => {
    return permissions.includes('users:read') || permissions.includes('users:create');
  }
);

export const selectCanSubmitClaimsJWT = createSelector(
  [selectJWTPermissions],
  (permissions) => {
    return permissions.includes('claims:submit');
  }
);

export const selectCanProcessClaimsJWT = createSelector(
  [selectJWTPermissions],
  (permissions) => {
    return permissions.includes('claims:review') || 
           permissions.includes('claims:validate') ||
           permissions.includes('claims:approve');
  }
);

export const selectCanViewAllClaimsJWT = createSelector(
  [selectJWTPermissions],
  (permissions) => {
    return permissions.includes('claims:review') || 
           permissions.includes('claims:validate') ||
           permissions.includes('claims:approve') ||
           permissions.includes('payments:create');
  }
);

export const selectCanApproveClaimsJWT = createSelector(
  [selectJWTPermissions],
  (permissions) => {
    return permissions.includes('claims:approve');
  }
);

export const selectCanManagePaymentsJWT = createSelector(
  [selectJWTPermissions],
  (permissions) => {
    return permissions.includes('payments:create') || permissions.includes('payments:pay');
  }
);

// Legacy role-based selectors for backward compatibility
// Keep these to avoid breaking existing components
export const selectCanSubmitClaims = createSelector(
  [selectCurrentUser],
  (user) => {
    const roles = user?.userRoles?.map(ur => ur.role?.name) || [];
    return roles.some(role => ['HR', 'Broker', 'CRM'].includes(role));
  }
);

export const selectCanProcessClaims = createSelector(
  [selectCurrentUser],
  (user) => {
    const roles = user?.userRoles?.map(ur => ur.role?.name) || [];
    return roles.some(role => ['Claims_Officer', 'Approver'].includes(role));
  }
);

export const selectCanViewAllClaims = createSelector(
  [selectCurrentUser],
  (user) => {
    const roles = user?.userRoles?.map(ur => ur.role?.name) || [];
    return roles.some(role => ['Claims_Officer', 'Approver', 'Finance'].includes(role));
  }
);

export const selectCanApproveClaims = createSelector(
  [selectCurrentUser],
  (user) => {
    const roles = user?.userRoles?.map(ur => ur.role?.name) || [];
    return roles.some(role => ['Approver'].includes(role));
  }
);

export const selectCanManagePayments = createSelector(
  [selectCurrentUser],
  (user) => {
    const roles = user?.userRoles?.map(ur => ur.role?.name) || [];
    return roles.some(role => ['Finance'].includes(role));
  }
);

// Auth state selectors
export const selectAuthLoading = (state) => state.auth.isLoading;
export const selectAuthError = (state) => state.auth.isError;
export const selectAuthSuccess = (state) => state.auth.isSuccess;
export const selectAuthMessage = (state) => state.auth.message;
